import React, { useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    flexRender,
    ColumnDef,
    SortingState,
    PaginationState,
    OnChangeFn,
} from '@tanstack/react-table';

interface DataTableProps<TData> {
    data: TData[];
    columns: ColumnDef<TData, any>[];
    pagination?: PaginationState;
    sorting?: SortingState;
    onPaginationChange?: OnChangeFn<PaginationState>;
    onSortingChange?: OnChangeFn<SortingState>;
    pageCount?: number;
    totalRecords?: number;
    loading?: boolean;
    onRowClick?: (row: TData) => void;
    enableClientSidePagination?: boolean;
}

export function DataTable<TData>({
    data = [],
    columns,
    pagination: controlledPagination,
    sorting,
    onPaginationChange,
    onSortingChange,
    pageCount,
    totalRecords,
    loading = false,
    onRowClick,
    enableClientSidePagination = false,
}: DataTableProps<TData>) {
    // Internal state for client-side pagination availability
    const [internalPagination, setInternalPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            pagination: controlledPagination ?? internalPagination,
        },
        onSortingChange,
        onPaginationChange: onPaginationChange ?? setInternalPagination,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: enableClientSidePagination ? getPaginationRowModel() : undefined,
        manualPagination: !enableClientSidePagination,
        manualSorting: true, // we can support client sort later if needed
        pageCount: enableClientSidePagination ? undefined : pageCount,
    });

    if (loading && data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg border border-gray-200 shadow-sm min-h-[400px]">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mb-4"></div>
                <p className="text-gray-500 font-medium">Loading data...</p>
            </div>
        );
    }

    // Prepare pagination info
    const pageSize = table.getState().pagination.pageSize;
    const pageIndex = table.getState().pagination.pageIndex;
    const effectiveTotal = enableClientSidePagination ? data.length : (totalRecords ?? 0);

    // Calculate display range
    const startRow = pageIndex * pageSize + 1;
    const endRow = Math.min((pageIndex + 1) * pageSize, effectiveTotal);

    return (
        <div className="space-y-4">
            <div className="dashboard-card min-w-0">
                <div className="overflow-x-auto min-w-0">
                    <table className="dashboard-table">
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            className="group px-4 py-2.5 text-left text-[11px] font-semibold text-orange-800 uppercase tracking-wider select-none"
                                            style={{ width: header.getSize() }}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    className={`flex items-center gap-1 ${header.column.getCanSort() ? 'cursor-pointer hover:text-gray-700' : ''
                                                        }`}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                    {header.column.getCanSort() && (
                                                        <span className="text-gray-400">
                                                            {{
                                                                asc: <span className="text-orange-600">↑</span>,
                                                                desc: <span className="text-orange-600">↓</span>,
                                                            }[header.column.getIsSorted() as string] ?? (
                                                                    <span className="text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                        ↕
                                                                    </span>
                                                                )}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {table.getRowModel().rows.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={columns.length}
                                        className="dashboard-table-empty"
                                    >
                                        <div className="flex flex-col items-center justify-center">
                                            <span className="text-2xl mb-2">0</span>
                                            <p>No records found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className={`transition-colors duration-150 ease-in-out ${onRowClick ? 'cursor-pointer' : ''}`}
                                        onClick={() => onRowClick && onRowClick(row.original)}
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td
                                                    key={cell.id}
                                                    className="px-4 py-2.5 text-[13px] text-gray-700 whitespace-nowrap"
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white px-4 py-2 border-t border-gray-200">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Rows per page:</span>
                        <select
                            className="form-select block w-16 pl-2 pr-6 py-1 text-xs border-gray-300 focus:outline-none focus:ring-orange-500 focus:border-orange-500 rounded-md shadow-sm"
                            value={table.getState().pagination.pageSize}
                            onChange={(e) => {
                                table.setPageSize(Number(e.target.value));
                            }}
                        >
                            {[10, 20, 50, 100].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    {pageSize}
                                </option>
                            ))}
                        </select>
                    </div>
                    <span className="text-xs text-gray-500">
                        {effectiveTotal > 0 ? (
                            <>
                                {startRow}-{endRow} of {effectiveTotal}
                            </>
                        ) : (
                            '0-0 of 0'
                        )}
                    </span>
                </div>

                <div className="flex items-center gap-1">
                    <button
                        className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                        title="Previous Page"
                    >
                        <span className="sr-only">Previous</span>
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <div className="flex items-center gap-1 px-2">
                        <span className="text-xs font-medium text-gray-700">
                            {table.getPageCount() > 0 ? pageIndex + 1 : 0}
                        </span>
                        <span className="text-xs text-gray-400">/</span>
                        <span className="text-xs text-gray-500">
                            {table.getPageCount()}
                        </span>
                    </div>

                    <button
                        className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                        title="Next Page"
                    >
                        <span className="sr-only">Next</span>
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DataTable;
